import { Elements } from '@stripe/react-stripe-js'
import { Badge } from '@viastud/ui/badge'
import { Button } from '@viastud/ui/button'
import { Card } from '@viastud/ui/card'
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@viastud/ui/dialog'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { SegmentedControl } from '@viastud/ui/segmented-control'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viastud/ui/select'
import { Crown, LoaderCircle, Rocket, Star, X } from 'lucide-react'
import { useState } from 'react'

import { getStripe } from '@/lib/stripe'
import type { Child } from '@/presenters/parent-subscription.presenter.ts'

import PaymentForm from '../payment-form'

const baseUrl: string = import.meta.env.VITE_WEB_BASE_URL

interface InactiveSubscriptionPanelProps {
  clientSecret: string | null
  subscriptionOpen: boolean
  onSubscriptionOpenChange: (open: boolean) => void
  children: Child[]
  onHandleSubscribe: (commitment: boolean, childId: string) => void
}

export function SubscriptionOffer({
  clientSecret,
  subscriptionOpen,
  onSubscriptionOpenChange,
  children,
  onHandleSubscribe,
}: InactiveSubscriptionPanelProps) {
  const { toast } = useToast()

  const OFFERS: { label: string; value: boolean }[] = [
    { label: 'Avec engagement', value: true },
    { label: 'Sans engagement', value: false },
  ] as const
  const [commitment, setCommitment] = useState(true)
  const [selectedChildId, setSelectedChildId] = useState('')

  const allChildrenSubscribed =
    children.length > 0 &&
    children.every((child) => child.status === 'active' || child.status === 'cancelled')

  return (
    <div className="flex flex-col gap-4">
      {/* Mise en avant de l'abonnement */}
      <Card className="border-none bg-blue-50/50 p-8 md:p-12">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex w-full flex-col items-center justify-between rounded-2xl bg-white p-6 md:flex-row md:gap-y-2">
            <div className="flex w-80 flex-col items-start justify-start">
              <div>
                {commitment && (
                  <Badge
                    variant="secondary"
                    className="mb-1 bg-blue-100 text-blue-700 hover:bg-blue-100"
                  >
                    Tarif préférentiel
                  </Badge>
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900">Abonnement illimité</h3>
              <SegmentedControl
                items={OFFERS}
                value={commitment}
                onChange={setCommitment}
                className="mt-2 self-start"
              />
            </div>
            <div className="flex w-60 flex-col items-center gap-y-4">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black text-gray-900">
                  {commitment ? '97€' : '149€'}
                </span>
                <span className="text-lg text-gray-500">/ mois</span>
              </div>

              {children.length > 0 && (
                <div className="w-full min-w-[200px]">
                  <Select
                    value={selectedChildId}
                    onValueChange={setSelectedChildId}
                    disabled={allChildrenSubscribed}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sélectionner un enfant" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {children
                        .filter((child) => child.status === 'ended' || child.status === 'inactive')
                        .map((child) => (
                          <SelectItem key={child.id} value={child.id}>
                            {child.firstName} {child.lastName}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                className="w-full"
                disabled={allChildrenSubscribed || children.length === 0}
                onClick={() => {
                  if (!selectedChildId) {
                    toast({
                      description: 'Vous devez sélectionner un enfant',
                      variant: 'destructive',
                    })
                  } else {
                    onHandleSubscribe(commitment, selectedChildId)
                  }
                }}
              >
                S&#39;abonner
              </Button>
              {children.length === 0 && (
                <small className="-mt-3 italic">Créez un enfant pour continuer</small>
              )}
            </div>
          </div>

          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-400">
              {commitment ? (
                <Star className="h-6 w-6 text-white" />
              ) : (
                <Crown className="h-6 w-6 text-white" />
              )}
            </div>
          </div>

          <h2 className="mb-2 text-3xl font-bold text-gray-900">
            Donnez le meilleur à votre enfant
          </h2>
          <p className="mb-10 max-w-2xl text-lg text-gray-600">
            Accès illimité à tous nos cours et ressources pour soutenir sa réussite scolaire
          </p>

          <div className="mb-12 grid w-full grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <Rocket className="h-5 w-5" />
              </div>
              <span className="text-center font-medium text-gray-700">
                Cours particuliers illimités pour votre enfant
              </span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <Crown className="h-5 w-5" />
              </div>
              <span className="text-center font-medium text-gray-700">
                Suivi de progression détaillé
              </span>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                <Star className="h-5 w-5" />
              </div>
              <span className="text-center font-medium text-gray-700">
                Ressources premium pour la réussite
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Dialog open={subscriptionOpen} onOpenChange={onSubscriptionOpenChange}>
        <DialogContent className="flex w-full max-w-6xl flex-col gap-6">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-gray-950">
              Informations de paiement
            </DialogTitle>
            <DialogClose asChild>
              <Button variant="none" className="m-0 h-6 w-6 justify-start p-0">
                <X className="h-6 w-6" />
              </Button>
            </DialogClose>
          </DialogHeader>

          {clientSecret ? (
            <Elements
              options={{
                clientSecret,
                appearance: {
                  theme: 'flat',
                  variables: {
                    colorPrimary: '#3347FF',
                    colorBackground: '#EBF4FF',
                    colorText: '#334155',
                    colorDanger: '#df1b41',
                    borderRadius: '16px',
                  },
                },
                loader: 'auto',
              }}
              stripe={getStripe()}
            >
              <div className="space-y-4">
                <PaymentForm returnUrl={`${baseUrl}/parent/settings`} />
              </div>
            </Elements>
          ) : (
            <div className="flex items-center justify-center py-8">
              <LoaderCircle className="h-8 w-8 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
